import { lazy, Suspense, useEffect, useRef, useState } from "react";
import PropType from 'prop-types';

const PdfFallbackViewer = lazy(() => import('./PdfFallbackViewer'));

PdfViewerComponent.propTypes = {
    document: PropType.any,
    height: PropType.string,
    fallbackToBrowser: PropType.bool,
};

export default function PdfViewerComponent(props) {
    const containerRef = useRef(null);
    const [useBrowserViewer, setUseBrowserViewer] = useState(false);

    useEffect(() => {
        const container = containerRef.current; // This `useRef` instance will render the PDF.

        let PSPDFKit;
        let isMounted = true;
        let loadTimer;
        setUseBrowserViewer(false);

        (async function () {
            if (!props.document) {
                return;
            }

            try {
                PSPDFKit = await import("pspdfkit");
                if (!isMounted) return;

                // Ensure that there's only one PSPDFKit instance.
                try { PSPDFKit.unload(container); } catch (e) { /* nothing loaded yet */ }

                const loadPromise = PSPDFKit.load({
                    // Container where PSPDFKit should be mounted.
                    container,
                    // The document to open.
                    document: props.document,
                    // Toolbar
                    toolbarItems: [{ type: "spacer" }, { type: "zoom-in" }, { type: "zoom-out" }, { type: "pager" }],

                    // Use the public directory URL as a base URL. PSPDFKit will download its library assets from here.
                    baseUrl: `${window.location.protocol}//${window.location.host}/${process.env.PUBLIC_URL}`,
                    licenseKey: `${process.env.REACT_APP_PSPDFKEY}`,
                    initialViewState: new PSPDFKit.ViewState({
                        scrollMode: PSPDFKit.ScrollMode.PER_SPREAD,
                        layoutMode: PSPDFKit.LayoutMode.SINGLE,
                    })
                });

                // Fail instead of hanging forever if the load never resolves
                const timeoutPromise = new Promise((_, reject) => {
                    loadTimer = setTimeout(() => reject(new Error('PSPDFKit.load timed out after 30s')), 30000);
                });

                await Promise.race([loadPromise, timeoutPromise]);
            } catch (err) {
                console.error('[PdfViewer] Error:', err);
                if (isMounted && props.fallbackToBrowser && typeof props.document === 'string') {
                    try { PSPDFKit?.unload(container); } catch (_) {}
                    setUseBrowserViewer(true);
                }
            } finally {
                clearTimeout(loadTimer);
            }
        })();

        return () => {
            isMounted = false;
            clearTimeout(loadTimer);
            try { PSPDFKit?.unload(container); } catch (e) { /* already unloaded */ }
        }
    }, [props.document, props.fallbackToBrowser]);


    // This div element will render the document to the DOM.
    return <div id="pdfDiv" ref={containerRef} style={{ width: "100%", height: props.height || "80vh" }}>
        {useBrowserViewer && <Suspense fallback={<div role="status">Loading PDF...</div>}>
            <PdfFallbackViewer document={props.document} />
        </Suspense>}
    </div>
}
